"use client";

import Link from "next/link";
import SectionWrapper from "../wrapper/SectionWrapper";
import Image from "next/image";
import { buildWebITData } from "@/data/buildWebIT.data";
import { IoCall, IoMail } from "react-icons/io5";
import { TransitionLink } from "../shared/TransitionLink";
import FacebookLink from "../shared/socials/FacebookLink";
import LinkedInIcon from "../shared/socials/LinkedInIcon";
import WhatsappIcon from "../shared/socials/WhatsappIcon";
import { useDict } from "@/components/providers/LocaleProvider";

const Footer = () => {
  const t = useDict();
  const year = new Date().getFullYear();

  return (
    <footer className="pt-4 lg:pt-16 bg-secondary z-30 text-white px-[5%]">
      <SectionWrapper className=" max-w-maxwidth mx-auto !px-0 !space-y-6">
        <div className="mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 items-start">
          {/* Logo */}
          <section className="flex flex-col items-start gap-2 col-span-2">
            <TransitionLink href="/">
              <Image
                src="/assets/logo/horizantal-white.svg"
                alt="Build Web IT Logo"
                width={200}
                height={200}
                className="object-contain"
              />
            </TransitionLink>
            <p className="mt-2">{t.footer.description}</p>

            <div className="mt-4 space-y-4">
              <p className="text-semibold  text-text">{t.footer.contactUs}</p>
              <div className=" space-y-2">
                <p className="flex items-center mt-1">
                  <span>
                    <IoMail size={24} className="mr-2 text-white" />
                  </span>
                  <span className="flex flex-col">
                    <Link
                      href={`mailto:${buildWebITData.email}`}
                      className="hover:underline transition-colors duration-300"
                    >
                      {buildWebITData.email}
                    </Link>
                    <Link
                      href={`mailto:${buildWebITData.infoEmail}`}
                      className="hover:underline transition-colors duration-300"
                    >
                      {buildWebITData.infoEmail}
                    </Link>
                  </span>
                </p>

                <p className="flex items-center mt-1">
                  <span>
                    <IoCall size={24} className="mr-2 text-white" />
                  </span>
                  <Link
                    href={`tel:${buildWebITData.phone}`}
                    className="hover:underline transition-colors duration-300"
                  >
                    {buildWebITData.phone}
                  </Link>
                </p>
              </div>
            </div>
          </section>

          {/* Site Links */}
          <div className="space-y-2">
            <p className="font-semibold mb-2 text-text">{t.footer.explore}</p>
            <ul className="space-y-1">
              <li className="hover:underline transition-colors duration-300">
                <Link href="/" aria-label={t.footer.exploreLinks.home}>
                  {t.footer.exploreLinks.home}
                </Link>
              </li>
              <li className="hover:underline transition-colors duration-300">
                <Link href="/projects" aria-label={t.footer.exploreLinks.projects}>
                  {t.footer.exploreLinks.projects}
                </Link>
              </li>
              <li className="hover:underline transition-colors duration-300">
                <Link href="/services" aria-label={t.footer.exploreLinks.services}>
                  {t.footer.exploreLinks.services}
                </Link>
              </li>
              <li className="hover:underline transition-colors duration-300">
                <Link href="/about-us" aria-label={t.footer.exploreLinks.about}>
                  {t.footer.exploreLinks.about}
                </Link>
              </li>
              <li className="hover:underline transition-colors duration-300">
                <Link href="/careers" aria-label={t.footer.exploreLinks.careers}>
                  {t.footer.exploreLinks.careers}
                </Link>
              </li>
              <li className="hover:underline transition-colors duration-300">
                <TransitionLink
                  href="/write-review"
                  aria-label={t.footer.exploreLinks.writeReview}
                >
                  {t.footer.exploreLinks.writeReview}
                </TransitionLink>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className="flex flex-col justify-between h-full mb-8">
            <p className="font-semibold mb-2 text-text">{t.footer.legal}</p>
            <ul className="space-y-1">
              <li className="hover:underline transition-colors duration-300">
                <Link
                  href="/terms-and-conditions"
                  aria-label={t.footer.legalLinks.termsAndConditions}
                >
                  {t.footer.legalLinks.termsAndConditions}
                </Link>
              </li>
              <li className="hover:underline transition-colors duration-300">
                <Link
                  href="/privacy-policy"
                  aria-label={t.footer.legalLinks.privacyPolicy}
                >
                  {t.footer.legalLinks.privacyPolicy}
                </Link>
              </li>
              <li className="hover:underline transition-colors duration-300">
                <Link
                  href="/cookie-policy"
                  aria-label={t.footer.legalLinks.cookiePolicy}
                >
                  {t.footer.legalLinks.cookiePolicy}
                </Link>
              </li>
            </ul>

            {/* Social Media Links */}
            <section className="mt-auto flex flex-col items-start space-y-2">
              <p className="font-semibold text-text">{t.footer.socials}</p>
              <div className="flex gap-4">
                <FacebookLink />
                <LinkedInIcon />
                <WhatsappIcon />
              </div>
            </section>
          </div>
        </div>
        <div>
          <div
            className="trustpilot-widget"
            data-locale="en-US"
            data-template-id="56278e9abfbbba0bdcd568bc"
            data-businessunit-id="68ea8bc01f4a2fbeae4ca8cf"
            data-style-height="52px"
            data-style-width="100%"
            data-style-padding="0px 0px 0px 0px"
            data-token="e0665cbe-038b-4ba2-bf96-1efb3d7a94a7"
          >
            <a
              href="https://www.trustpilot.com/review/buildweb-it.com"
              target="_blank"
              rel="noopener"
            >
              Trustpilot
            </a>
          </div>
        </div>
        {/* Social & Rights */}
        <section className="flex items-center justify-center w-full py-1 lg:py-2 border-t border-text/80">
          <p className="text-sm text-white mb-2">
            {t.footer.allRightsReserved.replace("{year}", String(year))}
          </p>
        </section>
      </SectionWrapper>
    </footer>
  );
};

export default Footer;
